import type { MedicalOptionData } from "@qendo/medical";
import type { ActivityOptionData } from "@qendo/profile/activities";
import type { SymptomOptionData } from "@qendo/profile/symptoms";
import type { TeamMemberOptionData } from "@qendo/profile/team";

import type { ColorValue } from "react-native/Libraries/StyleSheet/StyleSheet";
import type { CalenderEventOptionData, EventCategory } from "./calendar";
import * as calendar from "./calendar";
import type { ProfileRole } from "./profile";
import * as profile from "./profile";
import * as user from "./user";

export { calendar, profile, user };

export enum AgeRange {
    prefer_not_to_say = 0,
    below_eighteen = 1,
    eighteen_to_twenty_nine = 2,
    thirty_to_forty_five = 3,
    forty_six_to_fifty_nine = 4,
    sixty_to_seventy_nine = 5,
    eighty_and_over = 6,
}

export const AgeRangeOptions: AgeRange[] = [
    AgeRange.prefer_not_to_say,
    AgeRange.below_eighteen,
    AgeRange.eighteen_to_twenty_nine,
    AgeRange.thirty_to_forty_five,
    AgeRange.forty_six_to_fifty_nine,
    AgeRange.sixty_to_seventy_nine,
    AgeRange.eighty_and_over,
];

export const AgeRangeMap: Record<AgeRange, string> = {
    [AgeRange.prefer_not_to_say]: "Prefer not to say",
    [AgeRange.below_eighteen]: "Under 18",
    [AgeRange.eighteen_to_twenty_nine]: "18 - 29",
    [AgeRange.thirty_to_forty_five]: "30 - 45",
    [AgeRange.forty_six_to_fifty_nine]: "46 - 59",
    [AgeRange.sixty_to_seventy_nine]: "60 - 79",
    [AgeRange.eighty_and_over]: "80 and over",
};

export enum BloodType {
    a_positive = "A+",
    a_negative = "A-",
    b_positive = "B+",
    b_negative = "B-",
    o_positive = "O+",
    o_negative = "O-",
    ab_positive = "AB+",
    ab_negative = "AB-",
}

export enum Gender {
    female = "F",
    male = "M",
    non_binary = "T",
    prefer_not_to_say = "N",
}

export const GenderMap: Record<Gender, string> = {
    [Gender.female]: "Female",
    [Gender.male]: "Male",
    [Gender.non_binary]: "Non binary",
    [Gender.prefer_not_to_say]: "Prefer not to say",
};
export type CountryData = {
    code: string;
    name: string;
};

export enum OptionType {
    activities_bathings = "activities_bathings",
    activities_exercises = "activities_exercises",
    activities_exercise_durations = "activities_exercise_durations",
    activities_flight_durations = "activities_flight_durations",
    activities_sex_positions = "activities_sex_positions",
    activities_sex_results = "activities_sex_results",
    activities_tens = "activities_tens",
    allergies = "allergies",
    conditions = "conditions",
    immunisations = "immunisations",
    medications = "medications",
    supplements = "supplements",
    surgeries = "surgeries",
    past_symptoms = "past_symptoms",
    symptoms_acne_locations = "symptoms_acne_locations",
    symptoms_bleedings = "symptoms_bleedings",
    symptoms_bleeding_colours = "symptoms_bleeding_colours",
    symptoms_bowel_activities = "symptoms_bowel_activities",
    symptoms_discharges = "symptoms_discharges",
    symptoms_headaches = "symptoms_headaches",
    symptoms_ovulations = "symptoms_ovulations",
    symptoms_symptom_durations = "symptoms_symptom_durations",
    symptoms_symptom_locations = "symptoms_symptom_locations",
    symptoms_pain_types = "symptoms_pain_types",
    symptoms_treatments = "symptoms_treatments",
    symptoms_urine_colours = "symptoms_urine_colours",
    team_members = "team_members",
    triggers = "triggers",
    calendar_events = "calendar_events",
}

export type OtherableOptionType =
    | OptionType.activities_exercises
    | OptionType.allergies
    | OptionType.conditions
    | OptionType.immunisations
    | OptionType.medications
    | OptionType.past_symptoms
    | OptionType.supplements
    | OptionType.surgeries
    | OptionType.triggers
    | OptionType.symptoms_headaches
    | OptionType.symptoms_symptom_locations
    | OptionType.symptoms_treatments
    | OptionType.team_members
    | OptionType.calendar_events;

export const OtherableOptionTypes: OtherableOptionType[] = [
    OptionType.activities_exercises,
    OptionType.allergies,
    OptionType.conditions,
    OptionType.immunisations,
    OptionType.medications,
    OptionType.past_symptoms,
    OptionType.supplements,
    OptionType.surgeries,
    OptionType.triggers,
    OptionType.symptoms_headaches,
    OptionType.symptoms_symptom_locations,
    OptionType.symptoms_treatments,
    OptionType.team_members,
    OptionType.calendar_events,
];

export type MedicalOptionType =
    | OptionType.allergies
    | OptionType.conditions
    | OptionType.immunisations
    | OptionType.medications
    | OptionType.past_symptoms
    | OptionType.supplements
    | OptionType.surgeries
    | OptionType.triggers;

export type ActivityOptionType =
    | OptionType.activities_bathings
    | OptionType.activities_exercises
    | OptionType.activities_exercise_durations
    | OptionType.activities_flight_durations
    | OptionType.activities_sex_positions
    | OptionType.activities_sex_results
    | OptionType.activities_tens
    | OptionType.surgeries;

export type SymptomOptionType =
    | OptionType.symptoms_acne_locations
    | OptionType.symptoms_bleedings
    | OptionType.symptoms_bleeding_colours
    | OptionType.symptoms_bowel_activities
    | OptionType.symptoms_discharges
    | OptionType.symptoms_headaches
    | OptionType.symptoms_ovulations
    | OptionType.symptoms_symptom_durations
    | OptionType.symptoms_symptom_locations
    | OptionType.symptoms_pain_types
    | OptionType.symptoms_urine_colours
    | OptionType.symptoms_treatments;

export interface OptionData {
    title: string;
    id: string;
    colour?: string & ColorValue;
    group?: OptionGroup;
}

export type OptionGroup = EventCategory | ProfileRole;
export type ColouredOptionData = {
    color: string & ColorValue;
} & OptionData;
export type GroupedOptionData<T extends OptionGroup> = {
    group: T;
} & OptionData;

export type OptionTypeData<T extends OptionType = OptionType> = T extends MedicalOptionType
    ? MedicalOptionData<T>
    : T extends ActivityOptionType
      ? ActivityOptionData<T>
      : T extends SymptomOptionType
        ? SymptomOptionData<T>
        : T extends OptionType.team_members
          ? TeamMemberOptionData
          : T extends OptionType.calendar_events
            ? CalenderEventOptionData
            : OptionData;

export type OptionStore<T extends OptionType = OptionType> = Record<T, OptionTypeData<T>[]>;
export type OtherableOptionStore = OptionStore<OtherableOptionType>;

export interface InvalidResponse {
    message: string;
    errors: { [key: string]: string[] };
}

export type EnumLike<T> = {
    [id: string]: T | string;
    [nu: number]: string;
};

export type EnumValue<TEnum> = string & keyof EnumLike<TEnum>;

export type EnumKeyed<TKey, TValue> = {
    [key in EnumValue<TKey>]: TValue;
};

export type RecordList<TKey, TRecord> = EnumKeyed<TKey, TRecord[]>;

export interface ListData<TRecord> {
    data: TRecord[];
    total: number;
    updated: string;
}

export type MultiListData<TKey, TRecord> = {
    data: RecordList<TKey, TRecord>;
    total: number;
    updated: string;
};
